import { extractErrorInfo } from '@/services/walletService';

// Plain grouped-integer formatter (Latin digits, comma thousands separators). en-US pins the
// separator so grouping is identical across browsers/runtimes and matches the branch app
// (Vue3 + Flutter both comma-group). Shared by formatCurrency (money, suffixed with ກີບ) and
// formatNumber (counts, no unit). LAK has no minor unit → 0 fraction digits.
const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

/**
 * Format a numeric amount as LAK money: grouped digits + the Lao kip suffix (ກີບ).
 * e.g. -771000 -> "-771,000 ກີບ". Deterministic across runtimes — no locale currency
 * symbol lookup, which previously rendered the bare ISO code "LAK" as a prefix.
 * @param {number|string|null} amount
 * @returns {string}
 */
export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return '-';
  return `${numberFormatter.format(amount)} ກີບ`;
}

/**
 * Format a plain count with thousands separators (no currency unit).
 * e.g. 1234 -> "1,234". Used for transaction counts (ລາຍການ).
 * @param {number|string|null} value
 * @returns {string}
 */
export function formatNumber(value) {
  if (value === null || value === undefined) return '-';
  return numberFormatter.format(value);
}

/**
 * Format an error for user display, appending request ID when available.
 * @param {Error} error - Axios error or fetch-wrapped error
 * @param {string} defaultMsg - Fallback message if none extracted
 * @returns {string}
 */
export function formatErrorMessage(error, defaultMsg = 'ເກີດຂໍ້ຜິດພາດ') {
  const { message, requestId } = extractErrorInfo(error);
  const msg = message || defaultMsg;
  return requestId ? `${msg} (ref: ${requestId})` : msg;
}

/**
 * Format a date string. Supports Go API "DD-MM-YYYY HH:mm:ss" and ISO formats.
 * @param {string|null} dateString
 * @returns {string}
 */
export function formatDate(dateString) {
  if (!dateString) return '-';
  // Go API returns "DD-MM-YYYY HH:mm:ss" format
  const match = dateString.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (match) {
    const [, day, month, year, hour, minute] = match;
    return `${day}/${month}/${year} ${hour}:${minute}`;
  }
  // Fallback for ISO format
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('lo-LA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Truncate text to a given length with ellipsis.
 * @param {string|null} text
 * @param {number} length
 * @returns {string}
 */
export function truncate(text, length) {
  if (!text) return '-';
  return text.length > length ? text.substring(0, length) + '...' : text;
}

/**
 * Check if a transaction is a credit (incoming) for display.
 *
 * ROLLBACK is the only direction-ambiguous type: reversing a DEDUCT is a credit
 * (balance goes up) while reversing a REFUND/TOP_UP is a debit (balance goes down).
 * Classifying ROLLBACK purely by type mislabels one of the two cases (audit V-1 hit
 * the reduce-balance direction; the type-only fix then mislabeled the credit case),
 * so its direction is taken from the actual balance movement. Every other type stays
 * on the type allowlist. This mirrors Go's GetTransactionLabelForRow exactly
 * (internal/wallet/labels.go), which likewise only uses the balance delta for ROLLBACK.
 * @param {{ transaction_type: string, balance_before?: number|string, balance_after?: number|string }} tx
 * @returns {boolean}
 */
export function isCredit(tx) {
  if (tx.transaction_type === 'ROLLBACK') {
    const before = tx.balance_before;
    const after = tx.balance_after;
    // Only trust the delta when both balances are actually present — Number(null)
    // and Number('') both coerce to 0, which would fabricate a bogus delta.
    const hasBalances =
      before !== null && before !== undefined && before !== '' &&
      after !== null && after !== undefined && after !== '';
    if (hasBalances) {
      const nb = Number(before);
      const na = Number(after);
      if (Number.isFinite(nb) && Number.isFinite(na) && na !== nb) {
        return na > nb;
      }
    }
    // ROLLBACK with missing/equal balances falls through to debit (not in allowlist),
    // matching Go's static "ອອກ (ROLLBACK)" default.
  }
  // Non-ROLLBACK types classify by type. Keep aligned with Go's CreditTransactionTypes.
  // TRANSFER_IN is the incoming leg of a transfer (credit); TRANSFER_OUT falls through to debit.
  const creditTypes = ['TOP_UP', 'REFUND', 'ADJUSTMENT_CREDIT', 'TRANSFER_IN'];
  return creditTypes.includes(tx.transaction_type);
}

/**
 * Format a number with locale grouping for display in inputs.
 * @param {number|string|null} value
 * @returns {string}
 */
export function formatNumberInput(value) {
  if (value === null || value === undefined || value === '') return '';
  return numberFormatter.format(value);
}

/**
 * Parse a locale-formatted number string back to an integer.
 * @param {string} value
 * @returns {number}
 */
export function parseNumberInput(value) {
  if (!value) return 0;
  const cleaned = String(value).replace(/[^\d-]/g, '');
  return parseInt(cleaned, 10) || 0;
}

/**
 * Format an object or JSON string for display.
 * @param {object|string|null} obj
 * @param {number} indent
 * @returns {string}
 */
export function formatJson(obj, indent = 2) {
  if (!obj) return '-';
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return obj;
    }
  }
  return JSON.stringify(obj, null, indent);
}
